import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "@reach/router";
import { useStaticQuery, graphql } from "gatsby";
import { links } from "../../../config";
import siteConfig from "../../../config/site";
import { isVideo } from "../../utils/helpers/file";

const { address, contact, jobTitle } = siteConfig;

const SAME_AS = Object.values(links).filter(
  (url) =>
    typeof url === "string" &&
    /^https?:\/\//i.test(url) &&
    !url.startsWith("mailto:") &&
    !url.startsWith("tel:")
);

const Seo = ({
  title = "",
  description = "",
  image = "",
  article = false,
  datePublished = "",
  dateModified = "",
}) => {
  const { pathname } = useLocation();

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            titleTemplate
            siteUrl
            keywords
            description
            author
            lang
            image
          }
        }
      }
    `
  );

  const { siteMetadata } = site;
  const { siteUrl } = siteMetadata;

  const imagePath = image || siteMetadata.image;
  const resolvedImage = imagePath.startsWith("http")
    ? imagePath
    : `${siteUrl}${imagePath}`;

  // Social / Article image must be a still; fall back to site default for video covers
  const seoImage =
    resolvedImage && isVideo(resolvedImage)
      ? siteMetadata.image
      : resolvedImage;

  const seo = {
    title: title || siteMetadata.title,
    description: description || siteMetadata.description,
    image: seoImage,
    url: `${siteUrl}${pathname}`,
  };

  const isHomepage = pathname === "/";
  const personId = `${siteUrl}/#person`;
  const websiteId = `${siteUrl}/#website`;

  const person = {
    "@type": "Person",
    "@id": personId,
    name: siteMetadata.author,
    url: siteUrl,
    image: siteMetadata.image,
    jobTitle,
    email: contact.email,
    telephone: contact.phoneNumber,
    address: {
      "@type": "PostalAddress",
      addressLocality: address.city,
      addressRegion: address.region,
      addressCountry: address.country,
      postalCode: address.zipCode,
    },
    sameAs: SAME_AS,
  };

  const website = {
    "@type": "WebSite",
    "@id": websiteId,
    name: siteMetadata.title,
    url: siteUrl,
    description: siteMetadata.description,
    publisher: { "@id": personId },
    inLanguage: siteMetadata.lang || "en",
  };

  const graph = [website, person];

  if (article) {
    const published = datePublished || undefined;
    const modified = dateModified || datePublished || undefined;
    graph.push({
      "@type": "BlogPosting",
      "@id": `${seo.url}#article`,
      headline: seo.title,
      description: seo.description,
      image: seo.image,
      url: seo.url,
      mainEntityOfPage: seo.url,
      datePublished: published,
      dateModified: modified,
      author: { "@id": personId },
      publisher: { "@id": personId },
      inLanguage: siteMetadata.lang || "en",
    });
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": graph,
  };

  return (
    <Helmet
      title={seo.title}
      titleTemplate={isHomepage ? seo.title : siteMetadata.titleTemplate}
      htmlAttributes={{ lang: siteMetadata.lang }}
    >
      <meta
        name="viewport"
        content="minimum-scale=1, initial-scale=1, width=device-width"
      />
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      {seo.url && <link rel="canonical" href={seo.url} />}
      {seo.url && <meta property="og:url" content={seo.url} />}
      <meta property="og:type" content={article ? "article" : "website"} />
      {seo.title && <meta property="og:title" content={seo.title} />}
      {seo.description && (
        <meta property="og:description" content={seo.description} />
      )}
      {seo.image && <meta property="og:image" content={seo.image} />}
      <meta name="twitter:card" content="summary_large_image" />
      {seo.title && <meta name="twitter:title" content={seo.title} />}
      {seo.description && (
        <meta name="twitter:description" content={seo.description} />
      )}
      {seo.image && <meta name="twitter:image" content={seo.image} />}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default Seo;
